"use client";

import { DatePicker } from "@/components/portfolio/DatePicker";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Panel } from "@/components/ui/Panel";
import { Switch } from "@/components/ui/Switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/Tooltip";
import { useAppContext } from "@/context/AppContext";
import { useEstateContext } from "@/context/EstateContext";
import { useLanguage } from "@/context/LanguageContext";
import { useMenuItemsContext } from "@/context/MenuItemsContext";
import { Transaction } from "@/types";
import {
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { PlusIcon } from "@heroicons/react/24/solid";
import {
  BanknoteArrowDown,
  BanknoteArrowUp,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { TrashButton } from "@/components/tools/TrashButton";
import { RoundButtonWithToggleIcon } from "@/components/tools/RoundButtonWithToggleIcon";
import { RoundButtonsContainer } from "@/components/tools/RoundButtonsContainer";
import { MultiSelect } from "@/components/modals/MultiSelect";

export function AddNewTransaction() {
  const { language } = useLanguage();
  const copy = {
    cs: {
      income: "Příjem",
      expense: "Výdaj",
      once: "Jednorázově",
      monthly: "Měsíčně",
      yearly: "Ročně",
      title: "Upravit transakce",
      description: "Přidejte nebo upravte finanční transakce",
      addTransaction: "Přidat transakci",
      importedTransactions: "Vybrané transakce",
      affectedTransactions: "Ovlivněné transakce",
      willEndBefore: "Opakování bude ukončeno před {date}",
      period: "Vybraný měsíc",
      changePeriodTitle: "Přejít na jiný měsíc?",
      changePeriodDescription: "Neuložené změny v tomto měsíci budou ztraceny.",
      changePeriod: "Zahodit a pokračovat",
      replaceRecurringTitle: "Ukončit předchozí opakování?",
      replaceRecurringDescription:
        "Transakce „{name}“ je už aktivní. Chcete ji ukončit v {endDate}, aby nová začala v {startDate}?",
      keepBoth: "Ponechat obě",
      endPrevious: "Ukončit předchozí",
      emptyMonth:
        "Ve vybraném měsíci zatím není žádná transakce. Pomocí tlačítka výše můžete přidat novou.",
      transactionName: "Mé pojmenování transakce",
      paymentDate: "Měsíc platby",
      dateFrom: "Platí od měsíce",
      dateTo: "Platí do měsíce",
      currency: "Kč",
      locale: "cs-CZ",
      from: "od",
      deleteTransaction: "Smazat transakci",
      deleteTransactionTitle: "Opravdu chcete smazat transakci?",
      deleteTransactionDescription:
        "Opravdu chcete smazat transakci? Akci nelze vrátit.",
      collapseTransaction: "Sbalit transakci",
      expandTransaction: "Rozbalit transakci",
      amount: "Částka",
      transactionType: "Typ transakce",
      type: "Typ",
      yes: "ANO",
      no: "NE",
      includeTax: "Zahrnout do daňového přehledu:",
      taxOverview: "Daňový přehled",
      taxOverviewDescription:
        "Položky zahrnuté do daňového přehledu budou použity při generování daňového reportu a pomohou vám přesněji sledovat daňově uznatelné příjmy a výdaje.",
      deductible: "✓ Typicky uznatelné",
      nonDeductible: "✗ Typicky neuznatelné",
      rent: "Nájemné",
      repairs: "Opravy",
      insurance: "Pojištění",
      mortgageInterest: "Hypoteční úroky",
      waterDeposits: "Zálohy na vodu",
      heatElectricityDeposits: "Zálohy na teplo a elektřinu",
      tenantFees: "Poplatky hrazené nájemníkem",
      repairFund: "Fond oprav (SVJ)",
      recurrence: "Opakování",
      setRecurrenceEnd: "Nastavit konec opakování",
      saveReminder: "Nezapomeňte uložit změny.",
      cancelChanges: "Zrušit změny",
      saveChanges: "Uložit změny",
      leaveTitle: "Chcete odejít bez uložení změn?",
      leaveDescription: "Neuložené změny budou ztraceny.",
      cancel: "Zrušit",
      discardChanges: "Zahodit změny",
    },
    en: {
      income: "Income",
      expense: "Expense",
      once: "One-time",
      monthly: "Monthly",
      yearly: "Yearly",
      title: "Edit transactions",
      description: "Add or edit financial transactions",
      addTransaction: "Add transaction",
      importedTransactions: "Selected transactions",
      affectedTransactions: "Affected transactions",
      willEndBefore: "Recurrence will end before {date}",
      period: "Transaction month",
      changePeriodTitle: "Switch to another month?",
      changePeriodDescription: "Unsaved changes in this month will be lost.",
      changePeriod: "Discard and continue",
      replaceRecurringTitle: "End the previous recurrence?",
      replaceRecurringDescription:
        'The transaction "{name}" is already active. Do you want to end it in {endDate} so the new one can start in {startDate}?',
      keepBoth: "Keep both",
      endPrevious: "End previous",
      emptyMonth:
        "There are no transactions in the selected month yet. Use the button above to add one.",
      transactionName: "My transaction name",
      paymentDate: "Payment month",
      dateFrom: "Active from month",
      dateTo: "Active through month",
      currency: "CZK",
      locale: "en-US",
      from: "from",
      deleteTransaction: "Delete transaction",
      deleteTransactionTitle: "Do you really want to delete this transaction?",
      deleteTransactionDescription:
        "Do you really want to delete this transaction? This action cannot be undone.",
      collapseTransaction: "Collapse transaction",
      expandTransaction: "Expand transaction",
      amount: "Amount",
      transactionType: "Transaction type",
      type: "Type",
      yes: "YES",
      no: "NO",
      includeTax: "Include in tax overview:",
      taxOverview: "Tax overview",
      taxOverviewDescription:
        "Items included in the tax overview will be used when generating the tax report and help you track tax-deductible income and expenses more accurately.",
      deductible: "✓ Typically deductible",
      nonDeductible: "✗ Typically non-deductible",
      rent: "Rent",
      repairs: "Repairs",
      insurance: "Insurance",
      mortgageInterest: "Mortgage interest",
      waterDeposits: "Water deposits",
      heatElectricityDeposits: "Heating and electricity deposits",
      tenantFees: "Fees paid by tenant",
      repairFund: "Repair fund (HOA)",
      recurrence: "Recurrence",
      setRecurrenceEnd: "Set recurrence end",
      saveReminder: "Do not forget to save changes.",
      cancelChanges: "Cancel changes",
      saveChanges: "Save changes",
      leaveTitle: "Do you want to leave without saving changes?",
      leaveDescription: "Unsaved changes will be lost.",
      cancel: "Cancel",
      discardChanges: "Discard changes",
    },
  }[language];
  const transactionTypeLabels: Record<string, string> = {
    INCOME: copy.income,
    EXPENSE: copy.expense,
  };
  const transactionTypeOptions = [
    { value: "INCOME", label: copy.income },
    { value: "EXPENSE", label: copy.expense },
  ];
  const recurrenceLabels: Record<string, string> = {
    ONCE: copy.once,
    MONTHLY: copy.monthly,
    YEARLY: copy.yearly,
  };
  const recurrenceOptions = [
    { value: "ONCE", label: copy.once },
    { value: "MONTHLY", label: copy.monthly },
    { value: "YEARLY", label: copy.yearly },
  ];
  const czechMonthNames = {
    standalone: [
      "leden",
      "únor",
      "březen",
      "duben",
      "květen",
      "červen",
      "červenec",
      "srpen",
      "září",
      "říjen",
      "listopad",
      "prosinec",
    ],
    genitive: [
      "ledna",
      "února",
      "března",
      "dubna",
      "května",
      "června",
      "července",
      "srpna",
      "září",
      "října",
      "listopadu",
      "prosince",
    ],
    instrumental: [
      "lednem",
      "únorem",
      "březnem",
      "dubnem",
      "květnem",
      "červnem",
      "červencem",
      "srpnem",
      "zářím",
      "říjnem",
      "listopadem",
      "prosincem",
    ],
  };
  const formatTransactionMonth = (
    value: string,
    grammaticalCase: keyof typeof czechMonthNames = "standalone",
  ) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "—";
    if (language === "cs") {
      return `${czechMonthNames[grammaticalCase][date.getMonth()]} ${date.getFullYear()}`;
    }
    return new Intl.DateTimeFormat("en-US", {
      month: "long",
      year: "numeric",
    }).format(date);
  };
  const { isLoading } = useAppContext();
  const { setIsOpen } = useMenuItemsContext();
  const {
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    selectedEstate,
    defaultTransaction,
    transactionPreset,
    handleSubmitTransaction,
    monthTransactions,
  } = useEstateContext();

  const createEmptyTransaction = (preset = {}) => ({
    ...defaultTransaction,
    ...preset,
  });
  const mapTransactionsForForm = (transactions: Transaction[]) =>
    transactions.map((t: Transaction) => ({
      id: t.id,
      originalId: t.originalId,
      name: t.name ?? "",
      amount: t.amount ?? 0,
      transactionType: t.transactionType ?? "INCOME",
      recurrence: t.recurrence ?? "MONTHLY",
      dateFrom: t.originalDateFrom ?? t.dateFrom ?? "",
      originalDateFrom: t.originalDateFrom ?? "",
      dateTo: t.dateTo ?? null,
      updatedAt: t.updatedAt ?? "",
      isPrediction: t.isPrediction ?? false,
      taxDeductible: t.taxDeductible ?? false,
      showInDashboard: t.showInDashboard ?? true,
      bankImportId: t.bankImportId ?? null,
      bankImportSeriesId: t.bankImportSeriesId ?? null,
      replaceRecurringTransactionId: t.replaceRecurringTransactionId ?? null,
    }));
  const serializeTransactionsForComparison = (transactions: Transaction[]) =>
    JSON.stringify(
      transactions.map((transaction) => ({
        originalId: transaction.originalId ?? 0,
        name: transaction.name ?? "",
        amount: Number(transaction.amount) || 0,
        transactionType: transaction.transactionType ?? "INCOME",
        recurrence: transaction.recurrence ?? "MONTHLY",
        dateFrom: transaction.dateFrom ?? "",
        dateTo: transaction.dateTo ?? null,
        showInDashboard: transaction.showInDashboard ?? true,
        bankImportId: transaction.bankImportId ?? null,
        bankImportSeriesId: transaction.bankImportSeriesId ?? null,
        replaceRecurringTransactionId:
          transaction.replaceRecurringTransactionId ?? null,
      })),
    );
  const getInitialTransactions = () => {
    return mapTransactionsForForm(monthTransactions);
  };

  const [transactionsForm, setTransactionsForm] = useState<Transaction[]>([]);
  const isBankImportReview = (
    Array.isArray(transactionPreset)
      ? transactionPreset
      : transactionPreset
        ? [transactionPreset]
        : []
  ).some((preset) => Boolean(preset.bankImportId));
  const [transactionsToDelete, setTransactionsToDelete] = useState<
    Transaction[] | null
  >(null);
  const [expandedTransactionIndex, setExpandedTransactionIndex] = useState<
    number | null
  >(null);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [isExitConfirmOpen, setIsExitConfirmOpen] = useState(false);
  const [pendingPeriod, setPendingPeriod] = useState<{
    month: number;
    year: number;
  } | null>(null);
  const [pendingRecurringReplacement, setPendingRecurringReplacement] =
    useState<{
      transactionIndex: number;
      existingTransaction: Transaction;
      incomingTransaction: Transaction;
      replacementEndDate: string;
      transactions: Transaction[];
    } | null>(null);
  const isExitConfirmOpeningRef = useRef(false);
  const ignoreOutsideCloseUntilRef = useRef(0);
  const validationFlashTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    if (!selectedEstate?.transactions || !selectedMonth || !selectedYear)
      return;

    const mapped = mapTransactionsForForm(monthTransactions);
    if (transactionPreset) {
      ignoreOutsideCloseUntilRef.current = Date.now() + 500;
      const presets = Array.isArray(transactionPreset)
        ? transactionPreset
        : [transactionPreset];
      const mappedPresets = presets.map((preset) =>
        createEmptyTransaction({
          ...preset,
          dateFrom:
            preset.dateFrom ??
            new Date(selectedYear, selectedMonth - 1, 1).toISOString(),
        }),
      );
      setTransactionsForm(mappedPresets);
      setExpandedTransactionIndex(0);
    } else {
      setTransactionsForm(mapTransactionsForForm(monthTransactions));
      setExpandedTransactionIndex(null);
    }
    setShowValidationErrors(false);
  }, [
    defaultTransaction,
    isBankImportReview,
    monthTransactions,
    selectedEstate,
    selectedMonth,
    selectedYear,
    transactionPreset,
  ]);

  useEffect(
    () => () => {
      if (validationFlashTimeoutRef.current !== null) {
        window.clearTimeout(validationFlashTimeoutRef.current);
      }
    },
    [],
  );

  const addTransaction = () => {
    const periodStart = new Date(
      selectedYear,
      selectedMonth - 1,
      1,
      12,
    ).toISOString();
    const nextTransactions = [
      createEmptyTransaction({ dateFrom: periodStart }),
      ...transactionsForm,
    ];
    setTransactionsForm(nextTransactions);
    setExpandedTransactionIndex(0);
  };

  const updateTransaction = (
    index: number,
    field: keyof Transaction,
    value: any,
  ) => {
    setTransactionsForm((current) =>
      current.map((transaction, transactionIndex) =>
        transactionIndex === index
          ? { ...transaction, [field]: value }
          : transaction,
      ),
    );
  };

  const handleOutsideCloseAttempt = (event: Event) => {
    const target = event.target;
    if (
      target instanceof Element &&
      target.closest("[data-date-picker-popover]")
    ) {
      event.preventDefault();
      return;
    }

    if (!hasUnsavedChanges) return;

    event.preventDefault();
    if (Date.now() < ignoreOutsideCloseUntilRef.current) return;
    if (isExitConfirmOpeningRef.current) return;

    isExitConfirmOpeningRef.current = true;
    setIsExitConfirmOpen(true);
  };

  const removeTransaction = (index: number) => {
    const transaction = transactionsForm[index];
    const nextTransactions = transactionsForm.filter((_, i) => i !== index);

    setTransactionsForm(nextTransactions);
    setExpandedTransactionIndex((prev) => {
      if (nextTransactions.length === 0) return null;
      if (prev === null) return 0;
      if (prev === index) return Math.min(index, nextTransactions.length - 1);
      if (prev > index) return prev - 1;
      return prev;
    });
    setTransactionsToDelete((prev) =>
      (prev ?? []).some((t) => t.id === transaction.id)
        ? (prev ?? [])
        : [...(prev ?? []), transaction],
    );
  };

  const submitTransactions = async (nextTransactions: Transaction[]) => {
    const transactionsWithType = nextTransactions.map((t) => ({
      ...t,
      type: (t.originalId ?? 0) === 0 ? "ADD" : "EDIT",
    }));
    if (validationFlashTimeoutRef.current !== null) {
      window.clearTimeout(validationFlashTimeoutRef.current);
      validationFlashTimeoutRef.current = null;
    }
    setShowValidationErrors(false);
    await handleSubmitTransaction(transactionsWithType, true);

    if (
      transactionsToDelete !== null &&
      transactionsToDelete.length > 0 &&
      transactionsToDelete.map((t) => t.originalId).every((id) => id !== 0)
    ) {
      const transactionsToDeleteWithType = transactionsToDelete?.map((t) => ({
        ...t,
        type: "DELETE",
      }));
      setTransactionsToDelete(null);
      await handleSubmitTransaction(transactionsToDeleteWithType, false);
    }
  };

  const normalizeTransactionName = (name: string) =>
    name.trim().toLocaleLowerCase(language === "cs" ? "cs-CZ" : "en-US");

  const findRecurringReplacementCandidate = (incoming: Transaction) => {
    if (
      (incoming.originalId ?? 0) !== 0 ||
      incoming.recurrence === "ONCE" ||
      incoming.bankImportId ||
      incoming.replaceRecurringTransactionId != null ||
      !incoming.name.trim() ||
      !incoming.dateFrom
    ) {
      return null;
    }

    const incomingStart = new Date(incoming.dateFrom);
    if (Number.isNaN(incomingStart.getTime())) return null;
    const normalizedName = normalizeTransactionName(incoming.name);

    return (
      (selectedEstate?.transactions ?? [])
        .filter((candidate) => {
          const candidateStart = new Date(candidate.dateFrom);
          const candidateEnd = candidate.dateTo
            ? new Date(candidate.dateTo)
            : null;
          return (
            Number(candidate.id ?? candidate.originalId) > 0 &&
            candidate.recurrence !== "ONCE" &&
            candidate.transactionType === incoming.transactionType &&
            normalizeTransactionName(candidate.name) === normalizedName &&
            !Number.isNaN(candidateStart.getTime()) &&
            candidateStart < incomingStart &&
            (!candidateEnd ||
              Number.isNaN(candidateEnd.getTime()) ||
              candidateEnd >= incomingStart)
          );
        })
        .sort(
          (first, second) =>
            new Date(second.dateFrom).getTime() -
            new Date(first.dateFrom).getTime(),
        )[0] ?? null
    );
  };

  const reviewRecurringReplacements = (
    nextTransactions: Transaction[],
    startIndex = 0,
  ) => {
    for (let index = startIndex; index < nextTransactions.length; index += 1) {
      const incomingTransaction = nextTransactions[index];
      const existingTransaction =
        findRecurringReplacementCandidate(incomingTransaction);
      if (!existingTransaction) continue;

      const incomingStart = new Date(incomingTransaction.dateFrom);
      const existingStart = new Date(existingTransaction.dateFrom);
      const replacementEnd = new Date(incomingStart);
      replacementEnd.setMonth(replacementEnd.getMonth() - 1);
      if (replacementEnd < existingStart) {
        replacementEnd.setTime(existingStart.getTime());
      }
      setPendingRecurringReplacement({
        transactionIndex: index,
        existingTransaction,
        incomingTransaction,
        replacementEndDate: replacementEnd.toISOString(),
        transactions: nextTransactions,
      });
      return;
    }

    void submitTransactions(nextTransactions);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const firstInvalidTransactionIndex = transactionsForm.findIndex(
      (transaction) => !transaction.name.trim() || !transaction.dateFrom,
    );

    if (firstInvalidTransactionIndex !== -1) {
      if (validationFlashTimeoutRef.current !== null) {
        window.clearTimeout(validationFlashTimeoutRef.current);
      }
      setShowValidationErrors(false);
      setExpandedTransactionIndex(firstInvalidTransactionIndex);
      validationFlashTimeoutRef.current = window.setTimeout(() => {
        validationFlashTimeoutRef.current = null;
        setShowValidationErrors(true);
      }, 220);
      return;
    }

    reviewRecurringReplacements(transactionsForm);
  };

  const handleDiscardChanges = () => {
    isExitConfirmOpeningRef.current = false;
    setIsExitConfirmOpen(false);
    setPendingRecurringReplacement(null);
    setIsOpen(false);
    setTransactionsToDelete(null);
    setShowValidationErrors(false);

    const mapped = mapTransactionsForForm(monthTransactions);
    setTransactionsForm(mapped);
    setExpandedTransactionIndex(null);
  };

  const applyPeriod = (period: { month: number; year: number }) => {
    setTransactionsToDelete(null);
    setExpandedTransactionIndex(null);
    setShowValidationErrors(false);
    setSelectedYear(period.year);
    setSelectedMonth(period.month);
  };

  const handlePeriodChange = (value: string | null) => {
    if (!value) return;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return;

    const nextPeriod = {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
    if (
      nextPeriod.month === selectedMonth &&
      nextPeriod.year === selectedYear
    ) {
      return;
    }
    if (hasUnsavedChanges) {
      setPendingPeriod(nextPeriod);
      return;
    }
    applyPeriod(nextPeriod);
  };

  const hasUnsavedChanges =
    serializeTransactionsForComparison(transactionsForm) !==
    serializeTransactionsForComparison(getInitialTransactions());
  const affectedTransactions = React.useMemo(() => {
    const affectedById = new Map<
      number,
      { transaction: Transaction; replacementDate: string }
    >();

    transactionsForm.forEach((incomingTransaction) => {
      const affectedId = incomingTransaction.replaceRecurringTransactionId;
      if (!affectedId || !incomingTransaction.dateFrom) return;

      const affectedTransaction = selectedEstate?.transactions.find(
        (transaction) => Number(transaction.id) === Number(affectedId),
      );
      if (!affectedTransaction) return;

      affectedById.set(Number(affectedId), {
        transaction: affectedTransaction,
        replacementDate: incomingTransaction.dateFrom,
      });
    });

    return Array.from(affectedById.values());
  }, [selectedEstate, transactionsForm]);

  return (
    <>
      <DialogContent
        className="app-dialog-workspace gap-0 sm:max-w-[800px]"
        onInteractOutside={handleOutsideCloseAttempt}
      >
        <form
          noValidate
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="border-b border-softiq-primary/10 px-5 py-4 dark:border-white/10 sm:px-6">
            <DialogHeader className="pr-8">
              <DialogTitle className="app-section-eyebrow text-left">
                {copy.title}
              </DialogTitle>
              <DialogDescription className="mt-1 text-left text-sm font-medium text-softiq-primary dark:text-white">
                {copy.description}
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-softiq-primary/10 pt-4 dark:border-white/10">
              {!transactionPreset ? (
                <div className="w-full sm:w-48">
                  <p className="mb-1.5 text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40">
                    {copy.period}
                  </p>
                  <div className="-mb-2">
                    <DatePicker
                      id="transaction-period"
                      label={copy.period}
                      useDays={false}
                      disablePortal
                      value={new Date(
                        selectedYear,
                        selectedMonth - 1,
                        1,
                        12,
                      ).toISOString()}
                      onChange={handlePeriodChange}
                    />
                  </div>
                </div>
              ) : (
                <div />
              )}
              <Button
                type="button"
                variant="secondary"
                size="compact"
                onClick={addTransaction}
                className="w-full sm:w-auto"
              >
                <PlusIcon className="size-4" />
                {copy.addTransaction}
              </Button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-scroll [scrollbar-gutter:stable]">
            <div>
              {transactionPreset && transactionsForm.length > 0 && (
                <p className="px-5 py-3 text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40 sm:px-6">
                  {copy.importedTransactions}
                </p>
              )}
              {transactionsForm.length === 0 && (
                <div className="m-5 rounded-xl border border-dashed border-softiq-primary/15 px-4 py-8 text-center text-xs text-softiq-primary/45 dark:border-white/15 dark:text-white/45 sm:m-6">
                  {copy.emptyMonth}
                </div>
              )}

              {transactionsForm.map((transaction, index) => {
                const isExpanded = expandedTransactionIndex === index;
                const isIncome = transaction.transactionType === "INCOME";
                const transactionName = transaction.name.trim();
                const dateFromLabel =
                  transaction.recurrence === "ONCE"
                    ? copy.paymentDate
                    : copy.dateFrom;
                const showNameError = showValidationErrors && !transactionName;
                const showAmountError =
                  showValidationErrors && !transaction.amount;
                const showDateFromError =
                  showValidationErrors && !transaction.dateFrom;
                const recurrenceLabel =
                  recurrenceLabels[transaction.recurrence] ??
                  transaction.recurrence;
                const typeLabel =
                  transactionTypeLabels[transaction.transactionType] ??
                  transaction.transactionType;
                const amountLabel = new Intl.NumberFormat(copy.locale).format(
                  Number(transaction.amount) || 0,
                );
                const dateRangeLabel =
                  transaction.recurrence === "ONCE"
                    ? formatTransactionMonth(transaction.dateFrom)
                    : transaction.dateTo
                      ? `${formatTransactionMonth(transaction.dateFrom)} – ${formatTransactionMonth(transaction.dateTo)}`
                      : `${copy.from} ${formatTransactionMonth(
                          transaction.dateFrom,
                          language === "cs" ? "genitive" : "standalone",
                        )}`;

                return (
                  <div
                    key={`${transaction.id ?? "new"}-${index}`}
                    className={`overflow-hidden border-t border-softiq-primary/10 transition-colors first:border-t-0 dark:border-white/10 ${
                      isExpanded
                        ? "bg-white/55 dark:bg-white/[0.035]"
                        : "hover:bg-white/35 dark:hover:bg-white/[0.025]"
                    }`}
                  >
                    <div>
                      <div className="flex items-stretch">
                        <button
                          type="button"
                          className="flex min-w-0 flex-1 items-center gap-3 px-5 py-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-softiq-primary/60 sm:px-6"
                          onClick={() =>
                            setExpandedTransactionIndex(
                              isExpanded ? null : index,
                            )
                          }
                          aria-expanded={isExpanded}
                          aria-controls={`transaction-details-${index}`}
                        >
                          <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-softiq-secondary-shade-100/80 dark:bg-white/10">
                            {isIncome ? (
                              <BanknoteArrowUp className="size-4 text-softiq-secondary-shade-700 dark:text-softiq-secondary" />
                            ) : (
                              <BanknoteArrowDown className="size-4 text-red-600 dark:text-red-400" />
                            )}
                          </span>

                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-xs font-medium text-softiq-primary dark:text-white">
                              {transactionName || copy.transactionName}
                            </span>
                            <span className="app-section-meta mt-1 block">
                              {dateRangeLabel} · {recurrenceLabel}
                            </span>
                          </span>

                          <span className="shrink-0 text-right">
                            <span
                              className={`block text-sm font-medium ${
                                isIncome
                                  ? "text-softiq-secondary-shade-700 dark:text-softiq-secondary"
                                  : "text-red-600 dark:text-red-400"
                              }`}
                            >
                              {isIncome ? "+" : "−"}
                              {amountLabel} {copy.currency}
                            </span>
                            <span className="app-section-meta mt-1 block">
                              {typeLabel}
                            </span>
                          </span>
                        </button>

                        <RoundButtonsContainer className="shrink-0 self-center pr-5 sm:pr-6">
                          <TrashButton
                            label={copy.deleteTransaction}
                            confirmTitle={copy.deleteTransactionTitle}
                            confirmDescription={
                              copy.deleteTransactionDescription
                            }
                            onClick={() => {
                              removeTransaction(index);
                            }}
                          />

                          <RoundButtonWithToggleIcon
                            icon={isExpanded ? ChevronUp : ChevronDown}
                            isActive={false}
                            onClick={() =>
                              setExpandedTransactionIndex(
                                isExpanded ? null : index,
                              )
                            }
                            tooltipTextActive={copy.collapseTransaction}
                            tooltipTextInactive={copy.expandTransaction}
                          />
                        </RoundButtonsContainer>
                      </div>
                    </div>

                    {isExpanded && (
                      <div
                        id={`transaction-details-${index}`}
                        className="border-t border-softiq-primary/10 bg-white/70 px-5 py-5 dark:border-white/10 dark:bg-white/[0.025] sm:px-6"
                      >
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          <div className="md:col-span-2">
                            <label
                              htmlFor={`transactionName-${index}`}
                              className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60"
                            >
                              {copy.transactionName}
                            </label>
                            <Input
                              id={`transactionName-${index}`}
                              type="text"
                              placeholder={copy.transactionName}
                              value={transaction.name ?? ""}
                              aria-invalid={showNameError}
                              onChange={(event) =>
                                updateTransaction(
                                  index,
                                  "name",
                                  event.target.value,
                                )
                              }
                              className={`h-9 w-full rounded-lg !mb-0 ${
                                showNameError
                                  ? "!border-red-500 !bg-red-50 !text-red-700 transition-all duration-300"
                                  : ""
                              }`}
                            />
                          </div>
                          <div>
                            <label
                              htmlFor={`amount-${index}`}
                              className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60"
                            >
                              {copy.amount}
                            </label>
                            <Input
                              id={`amount-${index}`}
                              type="number"
                              placeholder={copy.amount}
                              value={transaction.amount ?? ""}
                              aria-invalid={showAmountError}
                              onChange={(e) =>
                                updateTransaction(
                                  index,
                                  "amount",
                                  e.target.value,
                                )
                              }
                              className={`h-9 w-full rounded-lg !mb-0 ${
                                showAmountError
                                  ? "!border-red-500 !bg-red-50 !text-red-700 transition-all duration-300"
                                  : ""
                              }`}
                              min={0}
                            />
                          </div>

                          <div>
                            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60">
                              {copy.transactionType}
                            </p>
                            <MultiSelect
                              options={transactionTypeOptions.map(
                                ({ label }) => label,
                              )}
                              selected={
                                transactionTypeLabels[
                                  transaction.transactionType
                                ]
                                  ? [
                                      transactionTypeLabels[
                                        transaction.transactionType
                                      ],
                                    ]
                                  : []
                              }
                              setSelected={(values) => {
                                const selectedLabel = values.at(-1);
                                const selectedOption =
                                  transactionTypeOptions.find(
                                    ({ label }) => label === selectedLabel,
                                  );

                                if (!selectedOption) {
                                  return;
                                }

                                updateTransaction(
                                  index,
                                  "transactionType",
                                  selectedOption.value,
                                );
                              }}
                              selectionMode="single"
                              showSearch={false}
                              placeholder={copy.type}
                              className="mb-0 w-full"
                            />
                          </div>

                          <div className="flex min-h-14 items-center justify-between gap-3 rounded-lg border border-border/70 bg-hover/30 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60">
                                {copy.includeTax}
                              </p>
                              <p className="mt-0.5 text-xs text-foreground">
                                {transaction.showInDashboard
                                  ? copy.yes
                                  : copy.no}
                              </p>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                              <Switch
                                id={`tax-${index}`}
                                onClick={(e) => e.stopPropagation()}
                                checked={transaction.showInDashboard}
                                onCheckedChange={(v) =>
                                  updateTransaction(index, "showInDashboard", v)
                                }
                              />
                              <Dialog>
                                <DialogTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="unstyled"
                                    aria-label={copy.taxOverview}
                                    className="rounded-full p-1 text-foreground/60 hover:bg-hover hover:text-foreground"
                                  >
                                    <InformationCircleIcon className="size-5" />
                                  </Button>
                                </DialogTrigger>
                                <DialogContent className="max-w-[420px]">
                                  <DialogHeader>
                                    <DialogTitle>
                                      {copy.taxOverview}
                                    </DialogTitle>
                                    <DialogDescription>
                                      {copy.taxOverviewDescription}
                                    </DialogDescription>
                                  </DialogHeader>
                                  <div className="space-y-4 text-sm">
                                    <div>
                                      <p className="mb-1 font-medium text-green-600">
                                        {copy.deductible}
                                      </p>
                                      <ul className="list-inside list-disc space-y-1 text-foreground">
                                        <li>{copy.rent}</li>
                                        <li>{copy.repairs}</li>
                                        <li>{copy.insurance}</li>
                                        <li>{copy.mortgageInterest}</li>
                                      </ul>
                                    </div>
                                    <div>
                                      <p className="mb-1 font-medium text-red-500">
                                        {copy.nonDeductible}
                                      </p>
                                      <ul className="list-inside list-disc space-y-1 text-foreground">
                                        <li>{copy.waterDeposits}</li>
                                        <li>{copy.heatElectricityDeposits}</li>
                                        <li>{copy.tenantFees}</li>
                                        <li>{copy.repairFund}</li>
                                      </ul>
                                    </div>
                                  </div>
                                </DialogContent>
                              </Dialog>
                            </div>
                          </div>

                          <div>
                            <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60">
                              {copy.recurrence}
                            </p>
                            <MultiSelect
                              options={recurrenceOptions.map(
                                ({ label }) => label,
                              )}
                              selected={
                                recurrenceLabels[transaction.recurrence]
                                  ? [recurrenceLabels[transaction.recurrence]]
                                  : []
                              }
                              setSelected={(values) => {
                                const selectedLabel = values.at(-1);
                                const selectedOption = recurrenceOptions.find(
                                  ({ label }) => label === selectedLabel,
                                );

                                if (!selectedOption) {
                                  return;
                                }

                                updateTransaction(
                                  index,
                                  "recurrence",
                                  selectedOption.value,
                                );
                                updateTransaction(index, "dateTo", null);
                              }}
                              selectionMode="single"
                              showSearch={false}
                              placeholder={copy.recurrence}
                              className="mb-0 w-full"
                            />
                          </div>

                          <div className="grid grid-cols-1 gap-4 md:col-span-2 sm:grid-cols-2">
                            <div>
                              <label
                                htmlFor={`dateFrom-${index}`}
                                className="mb-1.5 block text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60"
                              >
                                {dateFromLabel} *
                              </label>
                              <div className="-mb-2">
                                <DatePicker
                                  id={`dateFrom-${index}`}
                                  label={dateFromLabel}
                                  useDays={false}
                                  value={transaction.dateFrom}
                                  isInvalid={showDateFromError}
                                  onChange={(val) =>
                                    updateTransaction(index, "dateFrom", val)
                                  }
                                />
                              </div>
                            </div>

                            {transaction.recurrence !== "ONCE" && (
                              <div>
                                <p className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.12em] text-foreground/60">
                                  {copy.dateTo}
                                </p>
                                {transaction.dateTo === null ? (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="compact"
                                    className="w-full justify-start shadow-none"
                                    onClick={() =>
                                      updateTransaction(index, "dateTo", "")
                                    }
                                  >
                                    {copy.setRecurrenceEnd}
                                  </Button>
                                ) : (
                                  <div className="-mb-2">
                                    <DatePicker
                                      id={`dateTo-${index}`}
                                      label={copy.dateTo}
                                      useDays={false}
                                      value={transaction.dateTo}
                                      onChange={(val) =>
                                        updateTransaction(index, "dateTo", val)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}

              {isBankImportReview && affectedTransactions.length > 0 && (
                <div className="border-t border-softiq-primary/10 dark:border-white/10">
                  <div className="px-5 py-3 sm:px-6">
                    <p className="text-[9px] font-medium uppercase tracking-[0.14em] text-softiq-primary/40 dark:text-white/40">
                      {copy.affectedTransactions}
                    </p>
                  </div>

                  <div className="app-section-divider-top app-section-divide-y">
                    {affectedTransactions.map(
                      ({ transaction, replacementDate }) => {
                        const isIncome =
                          transaction.transactionType === "INCOME";
                        const amount = new Intl.NumberFormat(
                          copy.locale,
                        ).format(Number(transaction.amount) || 0);
                        const recurrence =
                          recurrenceLabels[transaction.recurrence] ??
                          transaction.recurrence;

                        return (
                          <div
                            key={transaction.id}
                            className="flex items-center gap-3 px-5 py-3 transition-colors hover:bg-white/35 dark:hover:bg-white/[0.025] sm:px-6"
                          >
                            <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-softiq-secondary/10">
                              {isIncome ? (
                                <BanknoteArrowUp className="size-4 text-softiq-secondary-shade-700 dark:text-softiq-secondary" />
                              ) : (
                                <BanknoteArrowDown className="size-4 text-red-600 dark:text-red-400" />
                              )}
                            </span>

                            <div className="min-w-0 flex-1">
                              <p className="truncate text-xs font-medium text-softiq-primary dark:text-white">
                                {transaction.name}
                              </p>
                              <p className="app-section-meta mt-1">
                                {recurrence} ·{" "}
                                {copy.willEndBefore.replace(
                                  "{date}",
                                  formatTransactionMonth(
                                    replacementDate,
                                    language === "cs"
                                      ? "instrumental"
                                      : "standalone",
                                  ),
                                )}
                              </p>
                            </div>

                            <div className="shrink-0 text-right">
                              <p
                                className={`text-sm font-medium ${
                                  isIncome
                                    ? "text-softiq-secondary-shade-700 dark:text-softiq-secondary"
                                    : "text-red-600 dark:text-red-400"
                                }`}
                              >
                                {isIncome ? "+" : "−"}
                                {amount} {copy.currency}
                              </p>
                              <p className="app-section-meta mt-1">
                                {transactionTypeLabels[
                                  transaction.transactionType
                                ] ?? transaction.transactionType}
                              </p>
                            </div>
                          </div>
                        );
                      },
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-softiq-primary/10 bg-white/80 px-5 py-3 dark:border-white/10 dark:bg-white/[0.025] sm:items-center sm:justify-between sm:px-6">
            {hasUnsavedChanges ? (
              <p className="text-xs text-foreground/60">{copy.saveReminder}</p>
            ) : (
              <span />
            )}
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                size="compact"
                onClick={handleDiscardChanges}
              >
                {copy.cancelChanges}
              </Button>
              <Button
                type="submit"
                disabled={isLoading}
                variant="default"
                size="compact"
                className="relative"
              >
                {isLoading ? (
                  <>
                    <span className="opacity-0">{copy.saveChanges}</span>
                    <span className="dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </span>
                  </>
                ) : (
                  copy.saveChanges
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>

        {isExitConfirmOpen && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Panel
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="transaction-leave-title"
              aria-describedby="transaction-leave-description"
              className="w-full max-w-md bg-background p-6 text-foreground shadow-2xl"
            >
              <h2 id="transaction-leave-title" className="app-dialog-title">
                {copy.leaveTitle}
              </h2>
              <p
                id="transaction-leave-description"
                className="app-dialog-description"
              >
                {copy.leaveDescription}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    isExitConfirmOpeningRef.current = false;
                    setIsExitConfirmOpen(false);
                  }}
                >
                  {copy.cancel}
                </Button>
                <Button type="button" onClick={handleDiscardChanges}>
                  {copy.discardChanges}
                </Button>
              </div>
            </Panel>
          </div>
        )}

        {pendingPeriod && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Panel
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="transaction-period-title"
              aria-describedby="transaction-period-description"
              className="w-full max-w-md bg-background p-6 text-foreground shadow-2xl"
            >
              <h2 id="transaction-period-title" className="app-dialog-title">
                {copy.changePeriodTitle}
              </h2>
              <p
                id="transaction-period-description"
                className="app-dialog-description"
              >
                {copy.changePeriodDescription}
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setPendingPeriod(null)}
                >
                  {copy.cancel}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    applyPeriod(pendingPeriod);
                    setPendingPeriod(null);
                  }}
                >
                  {copy.changePeriod}
                </Button>
              </div>
            </Panel>
          </div>
        )}

        {pendingRecurringReplacement && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Panel
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="transaction-replacement-title"
              aria-describedby="transaction-replacement-description"
              className="w-full max-w-md bg-background p-6 text-foreground shadow-2xl"
            >
              <h2
                id="transaction-replacement-title"
                className="app-dialog-title"
              >
                {copy.replaceRecurringTitle}
              </h2>
              <p
                id="transaction-replacement-description"
                className="app-dialog-description"
              >
                {copy.replaceRecurringDescription
                  .replace(
                    "{name}",
                    pendingRecurringReplacement.existingTransaction.name,
                  )
                  .replace(
                    "{endDate}",
                    formatTransactionMonth(
                      pendingRecurringReplacement.replacementEndDate,
                    ),
                  )
                  .replace(
                    "{startDate}",
                    formatTransactionMonth(
                      pendingRecurringReplacement.incomingTransaction.dateFrom,
                    ),
                  )}
              </p>
              <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const pending = pendingRecurringReplacement;
                    setPendingRecurringReplacement(null);
                    reviewRecurringReplacements(
                      pending.transactions,
                      pending.transactionIndex + 1,
                    );
                  }}
                >
                  {copy.keepBoth}
                </Button>
                <Button
                  type="button"
                  onClick={() => {
                    const pending = pendingRecurringReplacement;
                    const previousTransactionId = Number(
                      pending.existingTransaction.id ??
                        pending.existingTransaction.originalId,
                    );
                    const nextTransactions = pending.transactions.map(
                      (transaction, index) => {
                        if (index === pending.transactionIndex) {
                          return {
                            ...transaction,
                            replaceRecurringTransactionId:
                              previousTransactionId,
                          };
                        }
                        if (
                          Number(transaction.originalId) ===
                          previousTransactionId
                        ) {
                          return {
                            ...transaction,
                            dateTo: pending.replacementEndDate,
                          };
                        }
                        return transaction;
                      },
                    );
                    setTransactionsForm(nextTransactions);
                    setPendingRecurringReplacement(null);
                    reviewRecurringReplacements(
                      nextTransactions,
                      pending.transactionIndex + 1,
                    );
                  }}
                >
                  {copy.endPrevious}
                </Button>
              </div>
            </Panel>
          </div>
        )}
      </DialogContent>
    </>
  );
}
